import React, { useState, useEffect, useRef } from "react"
import { useGreenWaveDisplayPostMutation } from "../../store/Features/processData/greenWaveDisplay-api-slice"

import { Input } from "components"
import { IDirectionData, IDataFrame, ISignalChange, IGreenWaveDisplay } from "@types"
import openIcon from "./selecticon.png"
import { useParseQueryParams } from "./useParseQueryParams"
import { useLocation } from "react-router-dom"

interface IOptions {
 date: string
 greenWaveLeftToRight: boolean
 withoutStopping: boolean
 ptChainLeftToRight: boolean
 greenWaveRightToLeft: boolean
 withoutStoppingRightToLeft: boolean
 cycleSecond: boolean
 scale: number
}
export const GreenWaveDisplayGraph: React.FC = () => {
 const [fetchGreenWaveDisplay] = useGreenWaveDisplayPostMutation()

 const greenWaveArgs = useParseQueryParams()
 const location = useLocation()

 const queryParams = new URLSearchParams(location.search)

 const begin: string = queryParams.get("begin") || ""

 // Convert 'begin' to 'YYYY-MM-DDThh:mm' format
 const formatBeginForInput = (dateString: any): string => {
  if (!dateString) return ""

  const date = new Date(dateString)
  const offset = date.getTimezoneOffset() * 60000 // Convert offset to milliseconds
  // Correctly subtract the offset to get local time
  const localISOTime = new Date(date.getTime() - offset).toISOString().slice(0, -1)
  return localISOTime.slice(0, 16)
 }
 const signalStateRef = useRef()
 const [data, setData] = useState<IGreenWaveDisplay[]>([])
 const [allData, setAllData] = useState<IDirectionData[] | undefined>()
 const [allDataReversed, setAllDataReversed] = useState<IDirectionData[] | undefined>()
 const [isOpen, setIsOpen] = useState(false)
 // Example usage within a component
 const [option, setOption] = useState<IOptions>(() => {
  const initialBeginValue = formatBeginForInput(begin)
  return {
   date: initialBeginValue,
   greenWaveLeftToRight: false,
   withoutStopping: false,
   ptChainLeftToRight: false,
   greenWaveRightToLeft: false,
   withoutStoppingRightToLeft: false,
   cycleSecond: false,
   scale: 1.0,
  }
 })

 const [deltaToNext, setDeltaToNext] = useState<number[]>([0, 0])
 const [deltaToNextReversed, setDeltaToNextReversed] = useState<number[]>([0, 0])

 const sumOfNumArray = (...args: number[]): number => {
  return args.reduce((accumulator, current) => accumulator + current, 0)
 }

 useEffect(() => {
  fetchGreenWaveDisplay(greenWaveArgs)
   .then((res: any) => {
    setData(res.data)
   })
   .catch((e: any) => {
    console.log("e", e)
   })
 }, [])

 useEffect(() => {
  const allDeltaToNext: number[] = []
  const allDeltaToNextReversed: number[] = []

  if (data) {
   let filteredData: IDirectionData[] = []
   let filteredDataReversed: IDirectionData[] = []
   if (Array.isArray(data)) {
    // If data is an array, filter each item in the array
    filteredData = data[0]?.forward_direction.map((item: any, index: number) => {
     data[0]?.forward_direction.length - 1 !== index && allDeltaToNext.push(item.delta_to_next)

     const restructuredFrame: IDataFrame[] = item.data_frames.map((frame: IDataFrame) => {
      if (frame.signal_changes.length === 0) {
       frame = {
        ...frame,
        signal_changes: [
         {
          time_start: frame.frame_time_stamp,
          signal_state: "",
         },
        ],
       }
      }
      return { ...frame }
     })

     return {
      ...item,
      data_frames: restructuredFrame,
     } as IDirectionData // Explicit type assertion
    })

    // If data is an array, filter each item in the array for reversed direction
    filteredDataReversed = data[0]?.reverse_direction.map((item: any, index: number) => {
     data[0]?.reverse_direction.length - 1 !== index &&
      allDeltaToNextReversed.push(item.delta_to_next)

     const restructuredFrame: IDataFrame[] = item.data_frames.map((frame: IDataFrame) => {
      if (frame.signal_changes.length === 0) {
       frame = {
        ...frame,
        signal_changes: [
         {
          time_start: frame.frame_time_stamp,
          signal_state: "",
         },
        ],
       }
      }
      return { ...frame }
     })

     return {
      ...item,
      data_frames: restructuredFrame,
     } as IDirectionData // Explicit type assertion
    })
   }

   setAllData(filteredData)
   setAllDataReversed(filteredDataReversed)

   const sumOfAllDelta: number = sumOfNumArray(...allDeltaToNext)

   // Delta to next forward direction
   const _deltaToNext: number[] = [133]
   let lastSum = 133
   allDeltaToNext.forEach((delta) => {
    // Calculate the proportionate delta value and add it to the cumulative sum
    const cumulativeSum = (delta / sumOfAllDelta) * 1000 + lastSum
    lastSum = cumulativeSum
    _deltaToNext.push(cumulativeSum)
   })

   setDeltaToNext(_deltaToNext)

   // Delta to next Reversed direction
   const sumOfAllDeltaReversed: number = sumOfNumArray(...allDeltaToNextReversed)

   const _deltaToNextReversed: number[] = [1230]
   let lastSumReversed = 0 // Starting with 113 to meet the requirement

   allDeltaToNextReversed.forEach((delta) => {
    // Calculate the proportionate delta value and add it to the cumulative sum
    const cumulativeSum = (delta / sumOfAllDeltaReversed) * 1230 + lastSumReversed

    // Update the last sum with the new cumulative sum
    lastSumReversed = cumulativeSum

    // Add the cumulative sum to the array
    _deltaToNextReversed.push(1460 - cumulativeSum)
   })
   setDeltaToNextReversed(_deltaToNextReversed)
  }
 }, [data])

 const handleValues = (value: string, name: string) => {
  setOption((prevOption) => ({
   ...prevOption,
   [name]: value,
  }))
 }

 const [dragStart, setDragStart] = useState<number | null>(null)

 const handleDrag = (e: React.MouseEvent) => {
  if (dragStart !== null) {
   const deltaX = e.clientX - dragStart
   setOption((prevOption) => ({
    ...prevOption,
    scale: prevOption.scale + deltaX / 100,
   }))
  }
 }
 const toggleDropdown = () => {
  setIsOpen(!isOpen)
 }

 const getColorBasedOnSignalState = (signalState: any) => {
  switch (signalState) {
   case "00000011":
    return "green"
   case "00001100":
    return "yellow"
   case "00110000":
    return "red"
   case "00111100":
   case "00000000":
    return "black"
   case "00001111":
    return "red-yellow"
   // Add more cases as needed
   default:
    return "black" // Default color
  }
 }
 const containerHeight = 1178 * (allData?.length || 0)

 let prevTimestamp: any = null
 return (
  <div>
   <div className="flex relative w-[96%] mx-auto mt-5">
    <div className="flex flex-col Jakarta-font ">
     <Input
      className="outline-none bg-gray py-3 rounded px-3"
      label="Begin:"
      type="datetime-local"
      id="begin"
      name="date"
      value={option?.date}
      onChange={(value, name) => {
       handleValues(value, name)
      }}
     />
    </div>

    <div className="rounded fixed right-[3%] z-10 top-[25%] bg-gray shadow-2xl">
     <div className="relative inline-block text-left">
      <button
       onClick={toggleDropdown}
       type="button"
       className="inline-flex justify-center items-center w-full px-2.5 py-2.5 text-sm font-medium  bg-golden rounded-md focus:outline-none  active:bg-golden text-black"
      >
       Options
       <img
        className={`${isOpen ? "rotate-360" : "rotate-180"} w-2.5 ml-3`}
        src={openIcon}
        alt="icon"
       />
      </button>

      {isOpen && (
       <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-gray ring-1 ring-black ring-opacity-5">
        <div
         className="py-1"
         role="menu"
         aria-orientation="vertical"
         aria-labelledby="options-menu"
        >
         <div className="custom-checkbox2 relative flex justify-between items-center px-4 py-2.5">
          <input
           type="checkbox"
           id="greenWave"
           className="visually-hidden"
           name="greenWaveLeftToRight"
           checked={option?.greenWaveLeftToRight}
           onChange={(e: any) => {
            handleValues(e.target.checked, e.target.name)
           }}
          />
          <label className="font-semi font-sm Jakarta-font cursor-pointer" htmlFor={`greenWave`}>
           Green Wave L-{">"}R
          </label>
         </div>
         <div className="custom-checkbox2 relative flex justify-between items-center px-4 py-2.5">
          <input type="checkbox" id="withoutShopping" className="visually-hidden " />
          <label
           className="font-semi font-sm Jakarta-font cursor-pointer"
           htmlFor={`withoutShopping`}
          >
           Without Stopping L-{">"}R
          </label>
         </div>
         <div className="custom-checkbox2 relative flex justify-between items-center px-4 py-2.5">
          <input type="checkbox" id="ptChain" className="visually-hidden " />
          <label className="font-semi font-sm Jakarta-font cursor-pointer" htmlFor={`ptChain`}>
           PT Chain L-{">"}R
          </label>
         </div>
         <div className="custom-checkbox2 relative flex justify-between items-center px-4 py-2.5">
          <input
           type="checkbox"
           id="greenWaveR"
           className="visually-hidden"
           name="greenWaveRightToLeft"
           checked={option?.greenWaveRightToLeft}
           onChange={(e: any) => {
            handleValues(e.target.checked, e.target.name)
           }}
          />
          <label className="font-semi font-sm Jakarta-font cursor-pointer" htmlFor={`greenWaveR`}>
           Green Wave R-{">"}L
          </label>
         </div>
         <div className="custom-checkbox2 relative flex justify-between items-center px-4 py-2.5">
          <input type="checkbox" id="withoutShoppingR" className="visually-hidden " />
          <label
           className="font-semi font-sm Jakarta-font cursor-pointer"
           htmlFor={`withoutShoppingR`}
          >
           Without Stopping R-{">"}L
          </label>
         </div>
         <div className="custom-checkbox2 relative flex justify-between items-center px-4 py-2.5">
          <input type="checkbox" id="ptChainR" className="visually-hidden " />
          <label className="font-semi font-sm Jakarta-font cursor-pointer" htmlFor={`ptChainR`}>
           PT Chain R-{">"}L
          </label>
         </div>
         <div className="custom-checkbox2 relative flex justify-between items-center px-4 py-2.5">
          <input type="checkbox" id="cycleSecond" className="visually-hidden " />
          <label className="font-semi font-sm Jakarta-font cursor-pointer" htmlFor={`cycleSecond`}>
           Cycle Second
          </label>
         </div>
         <div className="relative flex justify-between items-center px-4 py-2.5">
          <label className="font-semi font-sm Jakarta-font cursor-pointer" htmlFor={`Scale`}>
           Scale
          </label>
          <input
           id="Scale"
           className="bg-gray py-2 outline-none rounded px-2 text-black w-2/5"
           type="number"
           name="scale"
           step="0.1"
           min="0.2"
           max="5.0"
           value={option?.scale}
           onChange={(e: any) => {
            handleValues(e.target.value, e.target.name)
           }}
          />
         </div>
        </div>
       </div>
      )}
     </div>
    </div>
   </div>
   <div
    className="relative hide-scroll-icon mt-5 z-0"
    style={{ overflowY: "scroll", height: "800px", position: "relative" }}
   >
    <svg
     width={`${876 + Number(option.scale) * 100}`}
     height={containerHeight}
     viewBox={`0 0 1440 ${containerHeight}`}
     fill="none"
     xmlns="http://www.w3.org/2000/svg"
     className="absolute left-0"
     preserveAspectRatio="none"
     onMouseDown={(e) => setDragStart(e.clientX)}
     onMouseMove={handleDrag}
     onMouseUp={() => setDragStart(null)}
    >
     <g width="100%" height="1178">
      <rect width="1440" height="1412" transform="translate(0 0.618408)" fill="#D0D0D0" />
      <path d="M115 73.1436H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 86.1491H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 86.1491H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 21.1212H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />

      <path d="M1315 21.1212H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 151.177H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 151.177H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 216.205H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 216.205H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 281.233H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 281.233H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 346.261H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 346.261H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 411.289H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 411.289H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 476.317H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 476.317H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 541.345H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 541.345H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 606.373H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 606.373H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 671.401H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 671.401H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 736.428H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 736.428H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 801.456H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 801.456H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 866.484H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 866.484H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 931.512H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 931.512H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 996.54H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 996.54H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1061.57H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1061.57H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1126.6H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1126.6H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 79.6464H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 79.6464H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 14.6184H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 14.6184H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 144.674H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 144.674H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 209.702H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 209.702H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 274.73H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 274.73H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 339.758H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 339.758H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 404.786H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 404.786H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 469.814H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 469.814H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 534.842H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 534.842H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 599.87H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 599.87H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 664.898H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 664.898H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 729.926H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 729.926H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 794.953H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 794.953H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 859.982H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 859.982H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 925.009H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 925.009H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 990.037H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 990.037H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1055.07H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1055.07H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1120.09H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1120.09H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 99.1547H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 99.1547H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 34.1268H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 34.1268H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 164.183H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 164.183H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 229.211H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 229.211H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 294.239H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 294.239H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 359.266H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 359.266H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 424.294H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 424.294H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 489.322H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 489.322H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 554.35H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 554.35H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 619.378H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 619.378H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 684.406H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 684.406H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 749.434H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 749.434H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 814.462H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 814.462H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 879.49H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 879.49H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 944.518H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 944.518H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1009.55H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1009.55H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1074.57H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1074.57H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1139.6H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1139.6H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 105.658H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 105.658H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 40.6296H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 40.6296H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 170.685H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 170.685H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 235.713H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 235.713H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 300.741H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 300.741H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 365.769H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 365.769H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 430.797H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 430.797H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 495.825H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 495.825H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 560.853H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 560.853H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 625.881H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 625.881H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 690.909H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 690.909H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 755.937H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 755.937H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 820.965H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 820.965H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 885.993H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 885.993H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 951.021H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 951.021H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1016.05H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 1016.05H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1081.08H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 1081.08H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1146.1H135" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1305 1146.1H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 112.16H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 112.16H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 47.1324H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 47.1324H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 177.188H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 177.188H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 242.216H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 242.216H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 307.244H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 307.244H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 372.272H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 372.272H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 437.3H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 437.3H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 502.328H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 502.328H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 567.356H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 567.356H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 632.384H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 632.384H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 697.412H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 697.412H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 762.44H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 762.44H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 827.468H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 827.468H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 892.495H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 892.495H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 957.524H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 957.524H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1022.55H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1022.55H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1087.58H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1087.58H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1152.61H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1152.61H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 118.663H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 118.663H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 53.6352H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 53.6352H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 183.691H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 183.691H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 248.719H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 248.719H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 313.747H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 313.747H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 378.775H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 378.775H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 443.803H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 443.803H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 508.831H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 508.831H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 573.859H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 573.859H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 638.887H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 638.887H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 703.914H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 703.914H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 768.942H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 768.942H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 833.97H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 833.97H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 898.998H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 898.998H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 964.026H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 964.026H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1029.05H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1029.05H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1094.08H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1094.08H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1159.11H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1159.11H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 125.166H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 125.166H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 60.1379H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 60.1379H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 190.194H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 190.194H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 255.222H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 255.222H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 320.25H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 320.25H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 385.278H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 385.278H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 450.306H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 450.306H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 515.333H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 515.333H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 580.361H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 580.361H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 645.389H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 645.389H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 710.417H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 710.417H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 775.445H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 775.445H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 840.473H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 840.473H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 905.501H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 905.501H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 970.529H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 970.529H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1035.56H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1035.56H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1100.58H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1100.58H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1165.61H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1165.61H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 131.669H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 131.669H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 66.6407H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 66.6407H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 196.697H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 196.697H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 261.725H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 261.725H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 326.752H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 326.752H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 391.78H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 391.78H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 456.808H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 456.808H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 521.836H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 521.836H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 586.864H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 586.864H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 651.892H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 651.892H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 716.92H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 716.92H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 781.948H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 781.948H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 846.976H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 846.976H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 912.004H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 912.004H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 977.032H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 977.032H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1042.06H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1042.06H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1107.09H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1107.09H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1172.12H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1172.12H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 92.6519H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 92.6519H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 27.624H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 27.624H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 157.68H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 157.68H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 222.708H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 222.708H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 287.736H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 287.736H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 352.764H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 352.764H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 417.792H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 417.792H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 482.82H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 482.82H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 547.847H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 547.847H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 612.875H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 612.875H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 677.903H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 677.903H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 742.931H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 742.931H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 807.959H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 807.959H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 872.987H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 872.987H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 938.015H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 938.015H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1003.04H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1003.04H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1068.07H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1068.07H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1133.1H125" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M1315 1133.1H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 138.172H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 203.199H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 268.227H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 333.255H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M30 398.283H1410" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 463.311H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 528.339H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 593.367H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 658.395H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 723.423H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M30 788.618H1410" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 853.479H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 918.507H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 983.535H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1048.56H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M115 1113.59H1325" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />
      <path d="M30 1178.62L1410 1178.62" stroke="#8B8989" strokeWidth="2" strokeLinecap="round" />

      {(option.greenWaveLeftToRight || option.greenWaveRightToLeft) &&
       allData &&
       allData.map((item, index) => {
        return (
         <g key={index}>
          {item.data_frames && (
           <g>
            {item.data_frames.map((frame: IDataFrame, frameIndex: number) => {
             const currentTimestamp = new Intl.DateTimeFormat("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              month: "numeric",
              day: "numeric",
              year: "numeric",
              hour12: true,
             }).format(new Date(frame.frame_time_stamp))

             // Check if the timestamp has changed
             const shouldShowTimestamp = currentTimestamp !== prevTimestamp

             // Update previous timestamp if it should be shown
             if (shouldShowTimestamp) {
              prevTimestamp = currentTimestamp
             }

             return (
              shouldShowTimestamp && (
               <React.Fragment key={frameIndex}>
                <text
                 x="1350"
                 y={1156.5 - frameIndex * 65}
                 fontFamily="Arial, sans-serif"
                 fontSize="14"
                 fontWeight="500"
                 fill="black"
                 textLength="70"
                >
                 {currentTimestamp.split(", ")[0]}
                </text>
                <text
                 key={index}
                 x={1350}
                 y={1174.5 - frameIndex * 65}
                 fontFamily="Arial, sans-serif"
                 fontSize="14"
                 fontWeight={frameIndex} // You can set a fixed fontWeight or adjust as needed
                 fill="black"
                 textLength="70"
                >
                 {currentTimestamp.split(", ")[1]}
                </text>
                <text
                 x={26}
                 y={1156.5 - frameIndex * 65}
                 fontFamily="Arial, sans-serif"
                 fontSize="14"
                 fontWeight="500"
                 fill="black"
                 textLength="70"
                >
                 {currentTimestamp.split(", ")[0]}
                </text>
                <text
                 key={index}
                 x={26}
                 y={1174.5 - frameIndex * 65}
                 fontFamily="Arial, sans-serif"
                 fontSize="14"
                 fontWeight={frameIndex} // You can set a fixed fontWeight or adjust as needed
                 fill="black"
                 textLength="70"
                >
                 {currentTimestamp.split(", ")[1]}
                </text>
               </React.Fragment>
              )
             )
            })}
           </g>
          )}
         </g>
        )
       })}

      {option.greenWaveLeftToRight &&
       allData &&
       allData.map((item, index) => {
        let prevCumulativeHeight = 1178 // Initialize with the initial value
        let lastSignalChangeTime: string | number | Date // Variable to store the timestamp of the last signalChange
        let prevColor: string | null = null // Variable to store the color based on the signal state from the previous item
        let preColorCode: string | null = null
        let isGreen: boolean
        // Initialize a variable to store the time_start when color changes from green to another color
        let greenToNonGreenTime: any
        let averageHeight: number | null = null
        // Store Previous DeltaToNext using useRef
        return (
         <g key={index}>
          <text
           x={
            index === 0
             ? deltaToNext[index + 1] / 2 + index * 133
             : index === 1
               ? deltaToNext[index] / 2 + deltaToNext[index + 1] / 2
               : 1590
           }
           y={"1318.41"}
           fontFamily="Arial, sans-serif"
           fontSize="20"
           fill="#01C001"
          >
           {item.delta_to_next + "m"}
          </text>
          <text
           x={deltaToNext[index] + 5}
           y="1309.85"
           fontFamily="Arial, sans-serif"
           fontSize="20"
           fill="#01c001"
          >
           {item.device_name}
          </text>
          <text
           x={deltaToNext[index] + 5}
           y="1243.79"
           fontFamily="Arial, sans-serif"
           fontSize="20"
           fill="#01c001"
          >
           {"1"}
          </text>
          {/* Device Lines At Bottom */}
          <rect x={deltaToNext[index]} y="1177.62" width="2" height="176" fill="#01C001" />
          <rect
           x={deltaToNext[index]}
           y="1318.96"
           width={
            index === 0
             ? deltaToNext[index + 1] - 133
             : deltaToNext[index + 1] - 133 - (deltaToNext[index] - 133)
           }
           height="2"
           fill="#01C001"
          />
          {/* Add arrowhead */}
          <polygon
           points={`${deltaToNext[index + 1] - 15},1313.5 ${deltaToNext[index + 1]},1321 ${
            deltaToNext[index + 1] - 15
           },1326.42`}
           fill="#01C001"
          />
          {item.data_frames &&
           item.data_frames.map((frame: IDataFrame, frameIndex: number) => {
            const isLastDataFrame = frameIndex === item.data_frames.length - 1
            let colorToUse
            let currentColor: any
            let greenHeightSum = 0 // Variable to store the sum of heights for green rectangles
            let prevCycleTime: string | null = null
            return (
             <React.Fragment key={frameIndex}>
              {frame.signal_changes.length &&
               frame.signal_changes.map((signalChange, signalIndex) => {
                lastSignalChangeTime = lastSignalChangeTime
                 ? lastSignalChangeTime
                 : frame.frame_time_stamp
                // Calculate the height
                const height =
                 frameIndex === 0
                  ? ((new Date(signalChange.time_start).getTime() -
                     new Date(frame.frame_time_stamp).getTime()) /
                     1000) *
                    6.48888888889
                  : ((new Date(signalChange.time_start).getTime() -
                     new Date(lastSignalChangeTime).getTime()) /
                     1000) *
                    6.48888888889
                // Update cumulative height
                const cumulativeHeight = prevCumulativeHeight - height

                // Update prevCumulativeHeight for the next iteration
                prevCumulativeHeight = cumulativeHeight

                lastSignalChangeTime = signalChange.time_start
                // Determine the current color

                currentColor = signalChange.signal_state
                 ? getColorBasedOnSignalState(signalChange.signal_state)
                 : getColorBasedOnSignalState(preColorCode)

                // Use the previous color if available, else use the current color
                // For the last data_frame, store the color for use in the next object
                colorToUse = isLastDataFrame ? currentColor : prevColor ?? currentColor
                isGreen = colorToUse === "green"

                // If current segment is green, add its height to the sum
                if (currentColor === "green" && prevColor !== "green") {
                 greenToNonGreenTime = cumulativeHeight
                } else if (
                 (currentColor !== "green" && prevColor === "green") ||
                 (item.data_frames.length - 1 === frameIndex && currentColor === "green")
                ) {
                 // When currentColor is not green and prevColor is green
                 // Calculate the average height and store it in averageHeight

                 averageHeight = (greenToNonGreenTime + cumulativeHeight) / 2 - 40
                }

                if (isGreen) {
                 greenHeightSum += height
                } else {
                 // If the color changed from green to another color, reset the sum
                 greenHeightSum = 0
                }
                // Update prevColor with the current color for the next iteration
                prevColor = currentColor
                signalStateRef.current = currentColor
                preColorCode = signalChange.signal_state ? signalChange.signal_state : preColorCode

                //Duplicate Current Cycle Time Remove
                const currentCycleTime: any = frame?.current_cycle_time

                // Check if currentCycleTime is equal to the previous cycle time
                const showEmpty = prevCycleTime === currentCycleTime

                // Update the previous cycle time for the next iteration
                prevCycleTime = currentCycleTime
                return (
                 <g key={signalIndex}>
                  {
                   <>
                    <text
                     key={frameIndex}
                     x={deltaToNext[index] + 20}
                     y={1174.8 - frameIndex * 65}
                     fontFamily="Arial, sans-serif"
                     fontSize="18"
                     fontWeight="500"
                     fill="black"
                    >
                     {showEmpty ? "" : frame?.current_cycle_time}
                    </text>

                    {colorToUse === "red-yellow" ? (
                     <>
                      <rect
                       x={deltaToNext[index]}
                       y={cumulativeHeight}
                       width="6"
                       height={height}
                       fill={"red"}
                      />
                      <rect
                       x={deltaToNext[index] + 6}
                       y={cumulativeHeight}
                       width="6"
                       height={height}
                       fill={"yellow"}
                      />
                     </>
                    ) : (
                     <rect
                      x={deltaToNext[index]}
                      y={cumulativeHeight}
                      width="12"
                      height={height}
                      fill={colorToUse}
                     />
                    )}
                    <rect
                     x={deltaToNext[index]}
                     y={cumulativeHeight}
                     width="12"
                     height={height}
                     fill={colorToUse}
                    />
                    {isGreen && option.greenWaveLeftToRight && (
                     <>
                      <rect
                       className="skew"
                       width={
                        index === 0
                         ? deltaToNext[index + 1] - 133
                         : deltaToNext[index + 1] - 133 - (deltaToNext[index] - 133)
                       }
                       height={greenHeightSum}
                       x={deltaToNext[index]}
                       y={cumulativeHeight}
                       fill="#00BF00"
                       fillOpacity="0.25"
                       style={{
                        transform: `skewY(-10deg)`,
                        transformOrigin: `${deltaToNext[index]}px ${cumulativeHeight}px`,
                       }}
                      />

                      {averageHeight && allData.length - 1 !== index && (
                       <text
                        x={
                         index === 0
                          ? deltaToNext[index + 1] / 2 + index * 133
                          : index === 1
                            ? deltaToNext[index] / 2 + deltaToNext[index + 1] / 2
                            : 1590
                        }
                        y={averageHeight}
                        dominantBaseline="center"
                        fontFamily="Arial, sans-serif"
                        fontSize="18"
                        fill="#01c001"
                        alignmentBaseline="middle"
                        textAnchor="center"
                        style={{ fontWeight: "bold" }}
                       >
                        {item.average_speed_to_next}
                        {" km/h"}
                       </text>
                      )}
                     </>
                    )}
                   </>
                  }
                 </g>
                )
               })}
             </React.Fragment>
            )
           })}
         </g>
        )
       })}

      {option.greenWaveRightToLeft &&
       allDataReversed &&
       allDataReversed.map((item, index) => {
        let prevCumulativeHeight = 1178 // Initialize with the initial value
        let lastSignalChangeTime: string | number | Date // Variable to store the timestamp of the last signalChange
        let prevColor: string | null = null // Variable to store the color based on the signal state from the previous item
        let preColorCode: string | null = null
        let isGreen: boolean
        // Initialize a variable to store the time_start when color changes from green to another color
        let greenToNonGreenTime: any
        return (
         <g key={index}>
          {index !== allDataReversed.length - 1 && (
           <text
            x={(deltaToNextReversed[index] + deltaToNextReversed[index + 1]) / 2}
            y={"1273.41"}
            fontFamily="Arial, sans-serif"
            fontSize="20"
            fill="purple"
           >
            {item.delta_to_next + "m"}
           </text>
          )}
          <text
           x={deltaToNextReversed[index] + 5}
           y="1270.85"
           fontFamily="Arial, sans-serif"
           fontSize="20"
           fill="purple"
          >
           {item.device_name}
          </text>
          <text
           x={deltaToNextReversed[index] + 5}
           y="1215.79"
           fontFamily="Arial, sans-serif"
           fontSize="20"
           fill="purple"
          >
           {"2"}
          </text>
          {/* Device Lines At Bottom */}
          <rect x={deltaToNextReversed[index]} y="1177.62" width="2" height="176" fill="purple" />
          <rect
           x={deltaToNextReversed[index]}
           y="1273.96"
           width={deltaToNextReversed[index] - deltaToNextReversed[index + 1]}
           height="2"
           fill="purple"
           transform={`translate(-${
            deltaToNextReversed[index] - deltaToNextReversed[index + 1]
           }, 0)`}
          />
          {index !== 0 && (
           <polygon
            points={`${deltaToNextReversed[index] + 10},1268.46 ${
             deltaToNextReversed[index]
            },1274.96 ${deltaToNextReversed[index] + 10},1281.46`}
            fill="purple"
           />
          )}

          {item.data_frames &&
           item.data_frames.map((frame: IDataFrame, frameIndex: number) => {
            const isLastDataFrame = frameIndex === item.data_frames.length - 1
            let colorToUse
            let currentColor: any
            let averageHeight: number = 0
            let greenHeightSum = 0 // Variable to store the sum of heights for green rectangles
            let prevCycleTime: string | null = null
            return (
             <React.Fragment key={frameIndex}>
              {frame.signal_changes.length &&
               frame.signal_changes.map((signalChange: ISignalChange, signalIndex: number) => {
                lastSignalChangeTime = lastSignalChangeTime
                 ? lastSignalChangeTime
                 : frame.frame_time_stamp
                // Calculate the height
                const height =
                 frameIndex === 0
                  ? ((new Date(signalChange.time_start).getTime() -
                     new Date(frame.frame_time_stamp).getTime()) /
                     1000) *
                    6.48888888889
                  : ((new Date(signalChange.time_start).getTime() -
                     new Date(lastSignalChangeTime).getTime()) /
                     1000) *
                    6.48888888889
                // Update cumulative height
                const cumulativeHeight = prevCumulativeHeight - height

                // Update prevCumulativeHeight for the next iteration
                prevCumulativeHeight = cumulativeHeight

                lastSignalChangeTime = signalChange.time_start
                // Determine the current color

                currentColor = signalChange.signal_state
                 ? getColorBasedOnSignalState(signalChange.signal_state)
                 : getColorBasedOnSignalState(preColorCode)

                // Use the previous color if available, else use the current color
                // For the last data_frame, store the color for use in the next object
                colorToUse = isLastDataFrame ? currentColor : prevColor ?? currentColor
                isGreen = colorToUse === "green"

                // If current segment is green, add its height to the sum
                if (currentColor === "green" && prevColor !== "green") {
                 greenToNonGreenTime = cumulativeHeight
                } else if (
                 (currentColor !== "green" && prevColor === "green") ||
                 (item.data_frames.length - 1 === frameIndex && currentColor === "green")
                ) {
                 // When currentColor is not green and prevColor is green
                 // Calculate the average height and store it in averageHeight

                 averageHeight = (greenToNonGreenTime + cumulativeHeight) / 2 - 30
                }

                if (isGreen) {
                 greenHeightSum += height
                } else {
                 // If the color changed from green to another color, reset the sum
                 greenHeightSum = 0
                }
                // Update prevColor with the current color for the next iteration
                prevColor = currentColor
                signalStateRef.current = currentColor
                preColorCode = signalChange.signal_state ? signalChange.signal_state : preColorCode

                //Duplicate Current Cycle Time Remove
                const currentCycleTime: any = frame?.current_cycle_time

                // Check if currentCycleTime is equal to the previous cycle time
                const showEmpty = prevCycleTime === currentCycleTime

                // Update the previous cycle time for the next iteration
                prevCycleTime = currentCycleTime
                return (
                 <g key={signalIndex}>
                  {
                   <>
                    <text
                     key={frameIndex}
                     x={deltaToNextReversed[index] + 20}
                     y={1174.8 - frameIndex * 65}
                     fontFamily="Arial, sans-serif"
                     fontSize="18"
                     fontWeight="500"
                     fill="black"
                    >
                     {showEmpty ? "" : frame?.current_cycle_time}
                    </text>
                    {colorToUse === "red-yellow" ? (
                     <>
                      <rect
                       x={deltaToNextReversed[index]}
                       y={cumulativeHeight}
                       width="6"
                       height={height}
                       fill={"red"}
                      />
                      <rect
                       x={deltaToNextReversed[index] + 6}
                       y={cumulativeHeight}
                       width="6"
                       height={height}
                       fill={"yellow"}
                      />
                     </>
                    ) : (
                     <rect
                      x={deltaToNextReversed[index]}
                      y={cumulativeHeight}
                      width="12"
                      height={height}
                      fill={colorToUse}
                     />
                    )}

                    {isGreen && option.greenWaveRightToLeft && (
                     <>
                      <rect
                       width={deltaToNextReversed[index] - deltaToNextReversed[index + 1]}
                       height={greenHeightSum}
                       x={deltaToNextReversed[index]}
                       y={cumulativeHeight}
                       fill="purple"
                       fillOpacity="0.25"
                       style={{
                        transform: `skewY(10deg) scaleX(-1)`, // Skew and flip horizontally
                        transformOrigin: `${deltaToNextReversed[index]}px ${cumulativeHeight}px`,
                       }}
                      />
                      {averageHeight && allDataReversed.length - 1 !== index && (
                       <text
                        x={(deltaToNextReversed[index] + deltaToNextReversed[index + 1]) / 2}
                        y={averageHeight}
                        dominantBaseline="center"
                        fontFamily="Arial, sans-serif"
                        fontSize="18"
                        fill="purple"
                        alignmentBaseline="middle"
                        textAnchor="center"
                        style={{ fontWeight: "bold" }}
                       >
                        {item.average_speed_to_next}
                        {" km/h"}
                       </text>
                      )}
                     </>
                    )}
                   </>
                  }
                 </g>
                )
               })}
             </React.Fragment>
            )
           })}
         </g>
        )
       })}
     </g>
    </svg>
   </div>
  </div>
 )
}
